import type { UseControllerProps } from "react-hook-form";

import { useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { TagInput } from "src/components/TagInput";
import { validateEmailList } from "src/utils/regex";
import { TextActionOptional } from "src/features/observations/components/TextActionOptional";

interface EmailRecipientQuestionProps extends UseControllerProps<ObservationFormState> {
	questionNumber: number;

	staticInput?: boolean;

	showAction?: boolean;
}

const EmailRecipientQuestion = ({
	name,
	questionNumber,
	staticInput = false,
	showAction = false
}: EmailRecipientQuestionProps) => {
	const { control, setError } = useFormContext<ObservationFormState>();

	const rules: UseControllerProps<ObservationFormState>["rules"] = {
		validate: (value: string) => validateEmailList(value)
	};

	const { field } = useController({ control, name, rules });
	const { value, onChange } = field;

	const initialRecipients = value
		?.split(",")
		.filter((email: string) => email !== "");

	const [additionalRecipients, setAdditionalRecipients] =
		useState<string[]>(initialRecipients);

	const handleOnInputChange = (value: string) => {
		if (staticInput) return;

		if (value === "") {
			setAdditionalRecipients([]);
		}

		onChange(value);
	};

	const handleTagsChange = (emails: string[]) => {
		if (staticInput) return;

		setAdditionalRecipients(emails);
	};

	const handleRemoveEmailTagClick = (email: string) => {
		if (staticInput) return;

		const newRecipients = additionalRecipients.filter(
			(recipient) => recipient !== email
		);

		setAdditionalRecipients(newRecipients);
		onChange(newRecipients.join(","));
	};

	const renderEmailTag = (email: string) => {
		return (
			<li key={email} className="inline-flex">
				<span className="inline-flex items-center gap-1 rounded-lg border border-border bg-white px-3 py-1 text-sm text-foreground">
					<span className="font-normal">{email}</span>

					{!staticInput && (
						<button
							type="button"
							aria-label={`Remove email (${email})`}
							onClick={() => handleRemoveEmailTagClick(email)}
							className="ml-1 text-foreground opacity-100 hover:opacity-70"
						>
							&times;
						</button>
					)}
				</span>
			</li>
		);
	};

	const handleOnTagLimitReached = () => {
		const name: UseControllerProps<ObservationFormState>["name"] = `questions.${
			questionNumber - 1
		}.givenAnswer`;

		const message =
			"You have exceeded the maximum number of allowed recipients (max 10)";

		setError(name, { message });
	};

	return (
		<div>
			<div className="mt-2">
				<TagInput
					value={value}
					type="email"
					placeholder="Please enter email(s)"
					tagsLimit={10}
					onTagLimitReached={handleOnTagLimitReached}
					onInputChange={handleOnInputChange}
					onTagsChange={handleTagsChange}
				/>
			</div>

			{additionalRecipients?.length > 0 && (
				<div className="mt-4">
					<p className="mb-2 font-bold">A copy of this form will be sent to:</p>

					<ul className="flex flex-wrap gap-4 list-none p-0 m-0">
						{additionalRecipients.map((email) => renderEmailTag(email))}
					</ul>
				</div>
			)}

			{showAction && (
				<TextActionOptional
					name={`questions.${questionNumber - 1}.action`}
					questionNumber={questionNumber}
					staticInput={staticInput}
				/>
			)}
		</div>
	);
};

export { EmailRecipientQuestion, type EmailRecipientQuestionProps };
